<!-- eslint-disable no-undef -->
<template>
  <div
    class="lottery-result-card"
    :class="{ 'lottery-result-card--cancelled': isCancelled }"
  >
    <div
      v-if="isCancelled"
      class="cancelled-overlay"
      role="img"
      :aria-label="$t('results.cancelled')"
    >
      <img
        v-if="!isLite && cancelledImgSrc"
        :src="cancelledImgSrc"
        alt=""
        class="cancelled-stamp"
        @error="useCancelledSvgFallback"
      >
      <span
        v-else-if="isLite"
        class="cancelled-stamp-lite"
      >{{ $t('results.cancelled') }}</span>
    </div>

    <div
      class="flag-section"
      @click="navigateToDetail"
    >
      <CountryAvatar
        v-if="isLite"
        :country="lottery?.country"
        :fallback-label="lottery?.game_name"
        size="lg"
      />
      <img
        v-else-if="lottery && lottery.game_logo"
        :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)"
        :alt="lottery.country"
        class="country-flag"
      >
      <img
        v-else
        src="/img/flags/default.svg"
        alt="Default Logo"
        class="country-flag"
      >
    </div>

    <div
      class="content-section"
      @click="navigateToDetail"
    >
      <div class="game-details">
        <h3>{{ lottery?.game_name }}</h3>
        <div class="draw-time">
          {{ formattedDrawTime }}
        </div>
      </div>

      <div
        v-if="!isCancelled"
        class="results"
      >
        <div class="numbers-wrap">
          <div 
            v-for="number in mainNumbers" 
            :key="'main-' + number"
            class="number-ball"
          >
            {{ number }}
          </div>
          <div 
            v-for="number in bonusNumbers" 
            :key="'bonus-' + number"
            class="number-ball bonus"
          >
            {{ number }}
          </div>
        </div>
      </div>
    </div>

    <div class="action-section">
      <div
        v-if="isMobile && !isResultsView"
        class="countdown-box"
      >
        <div class="countdown-block">
          <div class="countdown-number">
            {{ String(countdown.hours).padStart(2, '0') }}
          </div>
          <div class="countdown-label">
            {{ $t('time.hours') }}
          </div>
        </div>
        <div class="countdown-block">
          <div class="countdown-number">
            {{ String(countdown.minutes).padStart(2, '0') }}
          </div>
          <div class="countdown-label">
            {{ $t('time.mins') }}
          </div>
        </div>
        <div class="countdown-block">
          <div class="countdown-number">
            {{ String(countdown.seconds).padStart(2, '0') }}
          </div>
          <div class="countdown-label">
            {{ $t('time.secs') }}
          </div>
        </div>
      </div>
      <button
        v-if="!isMobile || isResultsView"
        class="bet-button"
        @click="$emit('bet')"
      >
        {{ $t('results.betOnNextDraw') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getGameLogoUrl,
  isResultCancelled,
  parseBonusNumbers,
  parseMainNumbers,
} from '@/utils/resultParser';
import { computed, defineProps, ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
import CountryAvatar from '@/components/base/CountryAvatar.vue'

/** Shared app asset — not theme-scoped (see public/img/cancelled.png). */
const CANCELLED_EVENT_IMAGE = '/img/cancelled.png';
const CANCELLED_EVENT_IMAGE_FALLBACK = '/img/cancelled.svg';

const router = useRouter();
const route = useRoute();
const { isLite } = useIntegrationMode();
const cancelledImgSrc = ref(CANCELLED_EVENT_IMAGE);

function useCancelledSvgFallback() {
  if (cancelledImgSrc.value !== CANCELLED_EVENT_IMAGE_FALLBACK) {
    cancelledImgSrc.value = CANCELLED_EVENT_IMAGE_FALLBACK;
  }
}

const props = defineProps<{
  lottery?: {
    game_name?: string
    game_slug?: string
    draw_date?: string | number | Date
    game_logo?: string
    country?: string
    result?: string | unknown
    cancelled?: boolean | string | number | null
    status?: string | null
  }
}>()

const isCancelled = computed(() => isResultCancelled(props.lottery ?? undefined));

const mainNumbers = computed(() => parseMainNumbers(props.lottery?.result));
const bonusNumbers = computed(() => parseBonusNumbers(props.lottery?.result));

const formattedDrawTime = computed(() => {
  const drawDate = props.lottery?.draw_date;
  if (!drawDate) return '';

  const dateString = String(drawDate).trim();
  let date: Date;

  // Normalize and parse
  if (dateString.includes('+')) {
    // Example: "2025-07-29 23:51:30+00:00"
    const [datePart, offset] = dateString.split('+');
    date = new Date(datePart.replace(' ', 'T') + '+' + offset);
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // "YYYY-MM-DD HH:mm:ss" → Assume UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return '';

  // Format as "29/07/2025, 23:51:30" in local time
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
});


const isMobile = computed(() => window.matchMedia('(max-width: 768px)').matches)

const isResultsView = computed(() => {
  // Check if we're in the Results view by looking at the current route
  return window.location.pathname.includes('/results')
})

const truncatedName = computed(() => {
  const game_name = props.lottery?.game_name || ''
  if (isMobile.value) return game_name
  return game_name.length > 15 ? game_name.slice(0, 15) + '...' : game_name
})

// Navigation function
const navigateToDetail = () => {
  if (props.lottery && props.lottery.game_slug) {
    router.push({ path: `/lottery/${props.lottery.game_slug}`, query: route.query });
  }
}

// Countdown logic
const countdown = ref({ hours: 0, minutes: 0, seconds: 0 })
let timer: number
const updateCountdown = () => {
  if (!props.lottery?.draw_date) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  const now = new Date()
  
  // Parse the UTC date string properly for countdown
  const dateString = String(props.lottery.draw_date)
  let drawTime: Date
  
  if (dateString.includes('+')) {
    // UTC format like "2025-07-29 23:51:30+00:00"
    const datePart = dateString.split('+')[0]
    drawTime = new Date(datePart + 'Z')
  } else {
    // Fallback for other formats
    drawTime = new Date(dateString)
  }
  
  const diff = drawTime.getTime() - now.getTime()
  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  }
}
onMounted(() => {
  countdown.value = { hours: 0, minutes: 0, seconds: 0 }
  updateCountdown()
  timer = window.setInterval(updateCountdown, 1000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<script lang="ts">
export default {
  name: 'LotteryResultCard'
}
</script>

<style scoped>
.lottery-result-card {
  /* background: var(--colour-surface); */
  background: var(--colour-surface-cont-lowest) !important;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) min-content;
  gap: var(--result-card-gap, 8px);
  padding: 0;
  align-items: stretch;
  cursor: pointer;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  border: var(--result-card-border-width, 1px) solid var(--result-card-border-color, var(--colour-surface-cont));
  border-radius: var(--result-card-radius, 8px);
  box-shadow: var(--result-card-shadow, none);
}

.lottery-result-card--cancelled {
  border-color: color-mix(in srgb, var(--colour-red-text-colour, #df2357) 35%, var(--colour-surface-cont));
}

.lottery-result-card--cancelled .flag-section,
.lottery-result-card--cancelled .content-section,
.lottery-result-card--cancelled .action-section {
  opacity: 0.42;
}

.cancelled-overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  pointer-events: none;
  overflow: hidden;
  border-radius: inherit;
}

.cancelled-stamp {
  position: absolute;
  top: 50%;
  left: 50%;
  height: 115%;
  width: auto;
  max-width: none;
  transform: translate(-50%, -50%);
  object-fit: contain;
  object-position: center;
  opacity: 0.92;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.25));
}

.flag-section {
  grid-row: span 2;
  display: flex;
  align-items: center;
  padding: var(--result-card-flag-padding, 4px 0 4px 10px);
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: var(--colour-surface);
  background: var(--colour-surface-cont-lowest) !important;
}

.flag-section:hover {
  background-color: var(--tab-button-inactive);
}

.country-flag {
  width: var(--result-card-flag-size, 50px);
  height: var(--result-card-flag-size, 50px);
  border-radius: 50%;
  object-fit: cover;
  border: var(--result-card-flag-border-width, 0) solid var(--colour-surface-cont-high);
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: var(--result-card-content-gap, 4px);
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: var(--result-card-content-padding, 4px 10px);
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: var(--colour-surface);
  background: var(--colour-surface-cont-lowest) !important;
}

.content-section:hover {
  background-color: var(--tab-button-inactive);
}

.game-details h3 {
  color: var(--result-card-title-color, var(--text-on-card-face));
  font-size: var(--result-card-title-size, 16px);
  font-weight: var(--result-card-title-weight, 500);
  margin: 0;
}

.draw-time {
  color: var(--result-card-time-color, var(--text-on-card-face-muted));
  font-size: var(--result-card-time-size, 14px);
  margin: 4px 0 0;
}

.results {
  display: flex;
  gap: var(--result-card-numbers-gap, 4px);
  position: relative;
  width: 100%;
  flex: 1;
}

.numbers-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: var(--result-card-numbers-gap, 4px);
  width: 100%;
  max-width: 100%;
}

@media (min-width: 769px) {
  /* Flag + middle content only — do not style .action-section / .bet-button here */
  .flag-section {
    align-items: center;
    padding: var(--result-card-inner-padding-block, 10px) 0
      var(--result-card-inner-padding-block, 10px)
      var(--result-card-inner-padding-inline, 10px);
  }

  .content-section {
    justify-content: center;
    padding: var(--result-card-inner-padding-block, 10px)
      var(--result-card-inner-padding-inline, 10px);
  }

  .results {
    flex: 0 0 auto;
  }

  .draw-time {
    margin-top: 2px;
    margin-bottom: 0;
  }

  .numbers-wrap :deep(.number-ball) {
    width: var(--result-card-ball-size, 28px);
    height: var(--result-card-ball-size, 28px);
    font-size: var(--result-card-ball-font-size, 12px);
    margin-bottom: 0;
  }
}


.action-section {
  grid-row: span 2;
  display: flex;
  margin: -1px;
}

.bet-button {
  background: var(--colour-surface-cont-low);
  color: var(--result-card-action-color, var(--text-on-card-contrast));
  border: none;
  padding: var(--result-card-action-padding, 0 12px);
  font-weight: var(--result-card-action-weight, 500);
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
  line-height: 1.4;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: var(--result-card-action-min-width, 120px);
}

.bet-button:hover {
  background: var(--bg-dark-tertiary);
}

@media (max-width: 768px) {
  .lottery-result-card {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    border-radius: var(--result-card-mobile-radius, var(--result-card-radius, 8px));
    min-width: 0;
    width: 100%;
    box-shadow: none;
    background: var(--colour-surface-cont-lowest);
    border: var(--result-card-mobile-border-width, var(--result-card-border-width, 1px)) solid var(--result-card-mobile-border-color, var(--result-card-border-color, var(--colour-surface-cont)));
    margin-bottom: 0;
    overflow: hidden;
  }

  .flag-section {
    grid-row: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 8px 10px 12px;
    flex: 0 0 auto;
    align-self: center;
  }

  .flag-section:hover {
    background-color: var(--tab-button-inactive);
  }

  .country-flag {
    width: var(--result-card-mobile-flag-size, 40px);
    height: var(--result-card-mobile-flag-size, 40px);
    margin: 0;
    border: 2px solid var(--colour-base-text-colour);
  }

  .content-section {
    grid-row: auto;
    grid-column: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    flex: 1 1 auto;
    padding: 10px 8px;
    min-width: 0;
  }

  .content-section:hover {
    background-color: var(--tab-button-inactive);
  }

  .game-details {
    width: 100%;
    min-width: 0;
  }

  .game-details h3 {
    color: var(--colour-base-text-colour);
    text-align: left;
    font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
    font-size: 10.685px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .game-details .draw-time {
    font-size: var(--result-card-mobile-time-size, 11px);
    font-weight: 400;
    color: var(--result-card-time-color, var(--colour-base-text-colour));
    margin: 2px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.2;
  }

  .results {
    display: flex !important;
    gap: 4px;
    align-items: center;
    margin-top: 0;
    margin-left: 0;
    width: 100%;
    min-width: 0;
  }

  .numbers-wrap {
    display: flex;
    flex-wrap: nowrap;
    gap: 4px;
    align-items: center;
    width: 100%;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .numbers-wrap::-webkit-scrollbar {
    display: none;
  }

  .numbers-wrap :deep(.number-ball) {
    flex-shrink: 0;
  }

  .numbers-wrap :deep(.number-ball.bonus) {
    background: var(--colour-surface-cont-lowest);
    border-color: var(--result-bonus-ball-border, var(--colour-orange-button, #f97316));
    border-width: 2px;
    color: var(--colour-base-text-colour);
  }

  .action-section {
    grid-row: auto;
    grid-column: auto;
    background: transparent;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    width: auto;
    align-self: stretch;
  }

  .bet-button {
    display: flex !important;
    background: var(--result-card-mobile-action-bg, var(--colour-surface-cont-low));
    color: var(--color, var(--colour-text-inverse));
    text-align: center;
    font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
    font-size: 11.495px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    border: none;
    padding: 8px 10px;
    border-radius: 0;
    cursor: pointer;
    transition: background-color 0.2s ease;
    white-space: normal;
    min-width: var(--result-card-mobile-action-min-width, 72px);
    max-width: var(--result-card-mobile-action-max-width, 88px);
    justify-content: center;
    align-items: center;
    text-transform: none;
    letter-spacing: 0;
    height: auto;
    align-self: stretch;
  }

  .bet-button:hover {
    background: var(--colour-surface-cont-high);
  }

  .countdown-box {
    background: var(--result-card-mobile-countdown-bg, var(--colour-surface-cont-low));
    border-radius: var(--result-card-mobile-countdown-radius, 4px);
    padding: 8px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    width: 100%;
    margin: 0 auto;
  }
  .countdown-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 32px;
    gap: 2px;
  }
  .countdown-number {
    font-size: 18px;
    font-weight: 700;
    color: var(--result-card-on-dark-text, var(--colour-text-inverse));
    line-height: 1;
    letter-spacing: 0.5px;
  }
  .countdown-label {
    font-size: 10px;
    color: var(--result-card-on-dark-text-muted, var(--colour-secondary-text-on-dark));
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.2px;
    line-height: 1;
    margin-top: 2px;
  }

  .cancelled-stamp {
    height: 120%;
    width: auto;
  }
}
</style>
